import type { StpNotification } from '~/components/ClaimPage/CliveApp/CliveNotifications/types';

import type { RunReply, ToldVia } from './types';

/**
 * The keys the log's conversation with Clive reads off an entry. `metadata` is a Firestore document, so
 * every value is checked rather than trusted: a value of the wrong shape reads as absent, and an entry
 * that carries one is drawn as the entry it would be without it.
 */

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isIdList = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === 'string');

const TOLD_VIA: ReadonlySet<string> = new Set<ToldVia>(['run_start', 'inbox']);

const isToldVia = (value: unknown): value is ToldVia => typeof value === 'string' && TOLD_VIA.has(value);

/** Whether the entry is its run's ending. By `run_event` alone: an ending's title says how it ended, not that it did. */
export const isRunEnded = (notification: StpNotification): boolean => notification.metadata?.run_event === 'ended';

/** The run that published the entry; null on one no run published. */
export const getRunId = (notification: StpNotification): string | null => {
  const runId = notification.metadata?.conductor_run_id;

  return typeof runId === 'string' ? runId : null;
};

/** How the run ended, on its ending: the outcome it finalized with. */
export const getDisposition = (notification: StpNotification): string | null => {
  const disposition = notification.metadata?.disposition;

  return typeof disposition === 'string' ? disposition : null;
};

/**
 * Clive's reply, on the ending of a run that gave one; null on any other entry, and on a reply whose message ids
 * or subject are missing, which no message could be matched to. A reply with no text is one whose questions are
 * the whole answer; one with no question ids raised none, as a reply published before replies carried questions.
 */
export const getRunReply = (notification: StpNotification): RunReply | null => {
  const reply = notification.metadata?.reply;
  if (!isRecord(reply)) return null;

  const { message_ids: messageIds, message_subject: messageSubject, text, ask_ids: askIds } = reply;
  const askIdList = askIds ?? [];
  if (!isIdList(messageIds) || !isIdList(askIdList) || typeof messageSubject !== 'string') return null;

  return { messageIds, messageSubject, text: typeof text === 'string' ? text : null, askIds: askIdList };
};

/**
 * The handler's messages a question was raised in reply to; none on a question of Clive's own, on a
 * request, and on a value that is not a list of ids.
 */
export const getInResponseTo = (notification: StpNotification): string[] => {
  const messageIds = notification.metadata?.in_response_to;

  return isIdList(messageIds) ? messageIds : [];
};

/** The handler's message a `handler_turn` entry is; null on a rejection note, which is not a message. */
export const getMessageId = (notification: StpNotification): string | null => {
  const messageId = notification.metadata?.message_id;

  return typeof messageId === 'string' ? messageId : null;
};

/** When the handler sent the message, on a `handler_turn` entry for one. */
export const getSentAt = (notification: StpNotification): string | null => {
  const sentAt = notification.metadata?.sent_at;

  return typeof sentAt === 'string' ? sentAt : null;
};

/** How the run that read the message was told it, on a `handler_turn` entry for one. */
export const getToldVia = (notification: StpNotification): ToldVia | null => {
  const toldVia = notification.metadata?.told_via;

  return isToldVia(toldVia) ? toldVia : null;
};
